import { useState } from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { Mail } from "lucide-react";
import FXBackdrop from "../components/FXBackdrop";
import { useAuth } from "../contexts/AuthContext";

const fade = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: false, amount: 0.1 }, transition: { duration: 0.6 } };
const inputCls = "h-11 rounded-xl border px-3 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 w-full";
const inputStyle = { borderColor: "rgba(226,232,240,0.9)" };

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function CompanySignUp() {
    const { sendMagicLink } = useAuth();
    const [email, setEmail] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function handleMagicLink(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await sendMagicLink(email, companyName);
            setSent(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <FXBackdrop>
            <section className="containerX pt-10 md:pt-16 pb-16">
                <motion.div {...fade} className="max-w-md mx-auto">
                    <div className="chip">
                        <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} />
                        For Companies
                    </div>
                    <h1 className="h1 mt-5 text-3xl md:text-4xl">Start hiring with hirrd</h1>
                    <p className="p mt-2">One-click sign in. No passwords to remember.</p>

                    <div className="glass p-6 md:p-8 mt-6">
                        {error && (
                            <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                                {error}
                            </div>
                        )}

                        {/* OAuth buttons */}
                        <div className="grid gap-3">
                            <a href={`${API}/api/auth/company/google`}
                                className="h-12 rounded-xl border flex items-center justify-center gap-3 font-medium hover:bg-slate-50 transition"
                                style={inputStyle}>
                                <svg width="18" height="18" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Sign in with Google
                            </a>
                        </div>

                        {/* Divider */}
                        <div className="flex items-center gap-3 my-6">
                            <div className="flex-1 h-px bg-slate-200" />
                            <span className="text-xs text-slate-400 uppercase tracking-wide">or use a magic link</span>
                            <div className="flex-1 h-px bg-slate-200" />
                        </div>

                        {/* Magic link form */}
                        {!sent ? (
                            <form onSubmit={handleMagicLink} className="grid gap-3">
                                <input className={inputCls} style={inputStyle} placeholder="Company name *" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
                                <input className={inputCls} style={inputStyle} placeholder="Work email *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                <div className="text-xs text-slate-500 -mt-1">
                                    Use your company email — Gmail/Outlook/Yahoo addresses are not accepted.
                                </div>
                                <button type="submit" disabled={loading} className="btn btnPrimary justify-center disabled:opacity-50">
                                    <Mail size={16} /> {loading ? "Sending..." : "Send login link"}
                                </button>
                            </form>
                        ) : (
                            <div className="text-center py-4">
                                <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                                    <Mail size={22} className="text-violet-600" />
                                </div>
                                <div className="font-semibold">Check your email</div>
                                <div className="text-sm text-slate-500 mt-1">
                                    We sent a login link to <span className="font-medium text-slate-700">{email}</span>
                                </div>
                                <button onClick={() => setSent(false)} className="text-sm text-violet-600 mt-3 hover:underline">
                                    Try a different email
                                </button>
                            </div>
                        )}

                        <div className="text-center text-sm text-slate-500 mt-6">
                            Already signed up? <NavLink to="/login" className="text-violet-600 font-medium hover:underline">Log in</NavLink>
                        </div>
                        <div className="text-center text-sm text-slate-500 mt-1">
                            Looking for work? <NavLink to="/signup/engineer" className="text-violet-600 font-medium hover:underline">Engineer sign up</NavLink>
                        </div>
                    </div>
                </motion.div>
            </section>
        </FXBackdrop>
    );
}
